import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Mail,
  MapPin,
  Globe,
  Clock,
  Phone,
  CheckCircle,
  Send,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Page Header
const PageHeader = () => {
  return (
    <section className="relative pt-32 pb-20 bg-[#0B2545]" data-testid="contact-header">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_1px,transparent_1px),linear-gradient(to_bottom,#ffffff12_1px,transparent_1px)] bg-[size:40px_40px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4"
          >
            Get In Touch
          </motion.p>
          <motion.h1 
            variants={fadeInUp}
            className="heading-font text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight leading-none mb-6"
          >
            Contact Us
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-lg text-slate-300 max-w-2xl"
          >
            Have questions about our services? Need a quote for your shipment? 
            Our team is here to help you find the perfect logistics solution.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Form Section
const ContactFormSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-white" data-testid="contact-form-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Form */}
            <motion.div variants={fadeInUp}>
              <div className="mb-8">
                <h2 className="heading-font text-2xl sm:text-3xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
                  Send Us a Message
                </h2>
                <p className="text-slate-600">
                  Fill out the form below and one of our logistics experts will get back to you 
                  within 24 hours.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#0B2545] mb-2">
                    Your Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] rounded-sm"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#0B2545] mb-2">
                    Email Address *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className="h-12 bg-slate-50 border-slate-200 focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] rounded-sm"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#0B2545] mb-2">
                    Your Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your shipping needs, cargo type, origin and destination..."
                    rows={6}
                    className="bg-slate-50 border-slate-200 focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] rounded-sm resize-none"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full h-12 rounded-sm"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message <Send className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div variants={fadeInUp}>
              <div className="bg-[#0B2545] p-8 lg:p-10 h-full relative overflow-hidden" data-testid="contact-info-card">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#F59E0B] opacity-10 rounded-full -translate-y-1/2 translate-x-1/2" />
                
                <h2 className="heading-font text-2xl font-bold text-white uppercase tracking-tight mb-8">
                  Contact Information
                </h2>
                
                <div className="space-y-8 relative">
                  {/* Address */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#0B2545]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Head Office</h4>
                      <p className="text-slate-300 leading-relaxed">
                        VISSERINGWEG 11<br />
                        1112 AS AMSTERDAM<br />
                        THE NETHERLANDS
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#0B2545]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Email Us</h4>
                      <a 
                        href="mailto:helpdesk@skycargodelivery.com" 
                        className="text-slate-300 hover:text-[#F59E0B] transition-colors block mb-1"
                        data-testid="contact-email-admin"
                      >
                        helpdesk@skycargodelivery.com
                      </a>
                      <a 
                        href="mailto:info@skycargodelivery.com" 
                        className="text-slate-300 hover:text-[#F59E0B] transition-colors block"
                        data-testid="contact-email-info"
                      >
                        info@skycargodelivery.com
                      </a>
                    </div>
                  </div>

                  {/* Website */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-[#0B2545]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Website</h4>
                      <p className="text-slate-300">skycargodelivery.com</p>
                    </div>
                  </div>

                  {/* Business Hours */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#F59E0B] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#0B2545]" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white mb-2">Business Hours</h4>
                      <p className="text-slate-300 text-sm">
                        Monday - Friday: 8:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 2:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/20 my-8" />

                {/* Quick Features */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F59E0B]" />
                    <span className="text-sm text-slate-300">24/7 Emergency Support Available</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F59E0B]" />
                    <span className="text-sm text-slate-300">Free Quote Within 24 Hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#F59E0B]" />
                    <span className="text-sm text-slate-300">Dedicated Account Manager</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Contact Us Section
const WhyContactSection = () => {
  const reasons = [
    {
      icon: MessageSquare,
      title: "Free Consultation",
      description: "Get expert advice on the best shipping solutions for your cargo at no cost."
    },
    {
      icon: Clock,
      title: "Quick Response",
      description: "We respond to all inquiries within 24 hours during business days."
    },
    {
      icon: Globe,
      title: "Global Solutions",
      description: "Access our worldwide network for shipping to over 50 countries."
    }
  ];

  return (
    <section className="py-24 bg-slate-50" data-testid="why-contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">Why Reach Out</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
              We're Here to Help
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Whether you need a quote, have questions about our services, or want to discuss 
              a custom logistics solution, our team is ready to assist.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white p-8 border border-slate-200 text-center"
                  data-testid={`why-contact-${index}`}
                >
                  <div className="w-16 h-16 bg-[#0B2545] flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-[#F59E0B]" />
                  </div>
                  <h3 className="heading-font text-xl font-bold text-[#0B2545] uppercase tracking-tight mb-3">
                    {reason.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {reason.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Map/Location Section
const LocationSection = () => {
  return (
    <section className="py-24 bg-white" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-[#F59E0B] mb-4">Our Location</p>
            <h2 className="heading-font text-3xl sm:text-4xl font-bold text-[#0B2545] uppercase tracking-tight mb-4">
              Find Us in Amsterdam
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our headquarters is strategically located in Amsterdam, one of Europe's most 
              important logistics hubs with excellent connections to major ports and airports.
            </p>
          </motion.div>

          <motion.div variants={fadeInUp} className="relative">
            {/* Map placeholder with Amsterdam image */}
            <div className="relative h-[400px] bg-slate-200 overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1534351590666-13e3e96b5017?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NzE2MTZ8MHwxfHNlYXJjaHwxfHxhbXN0ZXJkYW0lMjBjaXR5fGVufDB8fHx8MTcwMDAwMDAwMHww&ixlib=rb-4.0.3&q=85"
                alt="Amsterdam City"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#0B2545]/60" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <MapPin className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
                  <h3 className="heading-font text-2xl font-bold uppercase tracking-tight mb-2">
                    Sky Cargo Delivery
                  </h3>
                  <p className="text-slate-200">
                    VISSERINGWEG 11, 1112 AS AMSTERDAM, THE NETHERLANDS
                  </p>
                </div>
              </div>
            </div>

            {/* Location highlights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="flex items-center gap-4 p-4 bg-slate-50">
                <div className="w-10 h-10 bg-[#0B2545] flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="font-bold text-[#0B2545] text-sm">Schiphol Airport</p>
                  <p className="text-xs text-slate-600">15 minutes away</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-slate-50">
                <div className="w-10 h-10 bg-[#0B2545] flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="font-bold text-[#0B2545] text-sm">Port of Amsterdam</p>
                  <p className="text-xs text-slate-600">10 minutes away</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-slate-50">
                <div className="w-10 h-10 bg-[#0B2545] flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="font-bold text-[#0B2545] text-sm">Port of Rotterdam</p>
                  <p className="text-xs text-slate-600">1 hour away</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const ContactPage = () => {
  return (
    <div data-testid="contact-page">
      <PageHeader />
      <ContactFormSection />
      <WhyContactSection />
      <LocationSection />
    </div>
  );
};

export default ContactPage;
